import { RichTextInput } from '@/components/RichTextInput/RichTextInput';
import { useTimelineNote } from '@/hooks/useTimelineNote';
import { Button, Group, Paper, Text } from '@mantine/core';
import { formatHumanName, getReferenceString } from '@medplum/core';
import { Communication, Encounter, Reference } from '@medplum/fhirtypes';
import { useState } from 'react';
import { useTimelineContext } from '@/hooks/useTimelineContext';
import { JSONContent } from '@tiptap/react';

interface TimelineNoteInputProps {
  timelineItemReference: Reference<Communication | Encounter>;
  onCancel: () => void;
  onSave: () => void;
}

export function TimelineNoteInput(props: TimelineNoteInputProps) {
  const { timelineItemReference, onCancel, onSave } = props;

  const { careTeamContext } = useTimelineContext();
  const { careTeamProviders } = careTeamContext ?? {};
  const [content, setContent] = useState('');
  const [richTextContent, setRichTextContent] = useState<JSONContent>({});
  const { isSavingNote, createNote } = useTimelineNote();

  const handleSave = () => {
    createNote(timelineItemReference, content, richTextContent);
    setContent('');
    setRichTextContent({});
    onSave();
  };

  const handleCancel = () => {
    setContent('');
    setRichTextContent({});
    onCancel();
  };

  return (
    <Paper
      withBorder
      p="md"
      radius="md"
      bg="#fffbeb"
      pos="relative"
      mt="8px"
      style={{
        boxShadow: '0 4px 12px rgba(251, 191, 36, 0.15)',
        borderColor: '#FCECCC',
      }}
    >
      <Text variant="caption" mb="xs" fw={500}>
        Note
      </Text>

      <RichTextInput
        value={richTextContent}
        onChange={(text, json) => {
          setContent(text);
          setRichTextContent(json);
        }}
        disabled={isSavingNote}
        placeholder="Add a note about this item..."
        mentionItems={(careTeamProviders ?? []).map((provider) => ({
          id: getReferenceString(provider) ?? '',
          label: formatHumanName(provider.name?.[0]),
        }))}
      />

      <Group justify="flex-end" gap="xs" mt="sm">
        <Button
          variant="subtle"
          size="xs"
          color="gray"
          onClick={handleCancel}
          disabled={isSavingNote}
          style={{ textDecoration: 'none' }}
        >
          Cancel
        </Button>
        <Button
          variant="subtle"
          size="xs"
          color="blue"
          onClick={handleSave}
          disabled={!content.trim() || isSavingNote}
          loading={isSavingNote}
          style={{ textDecoration: 'none' }}
        >
          Save
        </Button>
      </Group>
    </Paper>
  );
}
